import {
  Component,
  ElementRef,
  Inject,
  ViewChild,
  OnInit,
  ChangeDetectorRef,
} from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { TableModule } from "primeng/table";
import { FormsModule } from "@angular/forms";
import { CommonModule } from "@angular/common";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";

import { DialogModule } from "primeng/dialog";
import { ToastModule } from "primeng/toast";
import { MessageService } from "primeng/api";

// Define Product interface OUTSIDE the class
interface Product {
  parameters: string;
  unit: string;
  type: number;
  code: string;
  uploadType: string;
  weight: number;
  formula: string;
  calculation: "" | "DMS" | "PUREBI";
  ranges: RangeRow[];
  criticalParameter: boolean;
  parameter_id?: number; // Add parameter_id field
  isAlreadyConfigured?: boolean; // Flag to indicate if parameter is already configured (for add parameter dialog)
}

interface RangeRow {
  score: number;
  min: number;
  max: number;
  selected: boolean;
  query: string;
  range_id?: number; // Optional range_id from backend
  loadedFromSavedConfig?: boolean; // Flag to indicate if range was loaded from saved config
  hasValidationError?: boolean; // Flag to indicate if range has validation error
}

@Component({
    selector: "app-module-config-dialog",
    templateUrl: "./module-config-dialog.component.html",
    styleUrls: ["./module-config-dialog.component.css"],
    imports: [
        TableModule,
        FormsModule,
        CommonModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatCheckboxModule,
        MatButtonModule,
        MatIconModule,
        DialogModule,
        ToastModule,
    ],
    providers: [MessageService]
})
export class ModuleConfigDialogComponent implements OnInit {
  module: any;
  moduleName: string;
  aliasName: string = "";
  moduleTypes: string[] = ["Type A", "Type B", "Type C"]; // Replace with backend data later
  moduleType: string = "";
  displayOnDashboard: boolean = false;
  positionNo: number | null = null;
  uploadTypes: string[] = ["Select", "Online", "Manual"];

  products: Product[] = [
    {
      parameters: "Parameter 1 jbsduwud",
      unit: "kg",
      type: 0,
      code: "P001",
      uploadType: "Manual",
      weight: 10,
      formula: "a+b",
      calculation: "",
      criticalParameter: false,
      ranges: [
        { score: 0, min: 1, max: 20, selected: false, query: "" },
        { score: 0, min: 21, max: 40, selected: false, query: "" },
        { score: 0, min: 41, max: 60, selected: false, query: "" },
        { score: 0, min: 61, max: 80, selected: false, query: "" },
        { score: 0, min: 81, max: 100, selected: false, query: "" },
      ],
    },
    {
      parameters: "RO Parameter 2",
      unit: "m3",
      type: 0,
      code: "P002",
      uploadType: "Online",
      weight: 10,
      formula: "c+d",
      calculation: "",
      criticalParameter: true,
      ranges: [
        { score: 0, min: 1, max: 20, selected: false, query: "" },
        { score: 0, min: 21, max: 40, selected: false, query: "" },
        { score: 0, min: 41, max: 60, selected: false, query: "" },
        { score: 0, min: 61, max: 80, selected: false, query: "" },
        { score: 0, min: 81, max: 100, selected: false, query: "" },
      ],
    },
  ];
  selectedProducts: Product[] = [];

  editorVisible = false;
  editorText = "";
  editorRange: any = null;
  hideHeaderFields = false;
  showParamSearch = false;
  showTypeSearch = false;
  showCodeSearch = false;
  globalSearchValue: string = "";
  searchError: string = "";
  searchInputFocused: boolean = false;
  formulaEditorVisible = false;
  formulaEditorText = "";
  formulaEditorProduct: any = null;

  editingProduct: any = null;
  originalProduct: any = null;
  gridCode: string = "";

  addParameterDialogVisible = false;
  addParamGlobalSearchValue: string = "";

  addParameterProducts: Product[] = [];
  private originalAddParameterProducts: Product[] = []; // Store original data for search restoration

  // Add Parameter Formula Editor properties
  addParamFormulaEditorVisible = false;
  addParamFormulaEditorText = "";
  private addParamFormulaEditorProduct: any = null;

  // Add Parameter Query Editor properties
  addParamEditorVisible = false;
  addParamEditorText = "";
  private addParamEditorRange: any = null;

  formulaEditorDialogVisible = false;
  formulaEditorDialogText = "";
  private formulaEditorDialogProduct: any = null;

  filteredModuleTypes: any[] = [];
  configuredGridModulesCount: number = 0;

  @ViewChild("aliasInput") aliasInput!: ElementRef<HTMLInputElement>;
  @ViewChild("dt") dt: any;
  @ViewChild("addParamTable") addParamTable: any; // Reference to add parameter table
  constructor(
    public dialogRef: MatDialogRef<ModuleConfigDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef
  ) {
    try {
      console.log("=== DIALOG CONSTRUCTOR DEBUG ===");
      console.log("Raw data received:", data);
      console.log("Module data:", data.module);

      this.module = data.module;
      this.moduleName = this.module?.module_name || "";
      this.hideHeaderFields = !!data.hideHeaderFields;
      this.gridCode = data.gridCode || "";

      console.log("Module name:", this.moduleName);
      console.log("Hide header fields:", this.hideHeaderFields);
      console.log("Grid code:", this.gridCode);

      // Get count of configured grid modules with displayOnDashboard enabled
      this.configuredGridModulesCount = data.configuredGridModulesCount || 0;

      // Restore input fields from module config
      this.aliasName = this.module.aliasName || this.module.module_alias || "";
      this.moduleType = this.module.moduleType || this.module.module_type || "";
      // Restore displayOnDashboard - check both camelCase and snake_case, prioritize camelCase
      this.displayOnDashboard =
        this.module.displayOnDashboard !== undefined
          ? this.module.displayOnDashboard
          : this.module.display_on_dashboard !== undefined
          ? !!this.module.display_on_dashboard
          : false;

      // Restore positionNo - check positionNo, position_no, and porder
      this.positionNo =
        this.module.positionNo !== undefined
          ? this.module.positionNo
          : this.module.position_no !== undefined
          ? this.module.position_no
          : this.module.porder !== undefined
          ? this.module.porder
          : null;

      // Debug: Log the restoration process
      console.log("=== FIELD RESTORATION DEBUG ===");
      console.log("Module object keys:", Object.keys(this.module));
      console.log("Looking for aliasName in module:", this.module.aliasName);
      console.log("Looking for moduleType in module:", this.module.moduleType);
      console.log(
        "Looking for module_alias in module:",
        this.module.module_alias
      );
      console.log(
        "Looking for module_type in module:",
        this.module.module_type
      );
      console.log("Restored aliasName:", this.aliasName);
      console.log("Restored moduleType:", this.moduleType);
      console.log("Looking for positionNo in module:", this.module.positionNo);
      console.log(
        "Looking for position_no in module:",
        this.module.position_no
      );
      console.log("Looking for porder in module:", this.module.porder);
      console.log("Restored positionNo:", this.positionNo);
      console.log("=== END FIELD RESTORATION DEBUG ===");

      console.log("Alias name:", this.aliasName);
      console.log("Module type:", this.moduleType);
      console.log("Display on dashboard:", this.displayOnDashboard);
      console.log("Module aliasName field:", this.module.aliasName);
      console.log("Module moduleType field:", this.module.moduleType);
      console.log("Module module_alias field:", this.module.module_alias);
      console.log("Module module_type field:", this.module.module_type);

      // Restore products from allparameters
      if (this.module && Array.isArray(this.module.allparameters)) {
        console.log("=== DIALOG: RESTORING PRODUCTS FROM ALLPARAMETERS ===");
        console.log("Dialog received module data:", this.module);
        console.log("Module allparameters:", this.module.allparameters);
        console.log("Number of parameters:", this.module.allparameters.length);

        this.products = this.module.allparameters.map(
          (param: any, idx: number) => {
            console.log(`\n=== PROCESSING PARAMETER ${idx + 1} ===`);
            console.log(
              `Parameter name: ${param.parameter_name || param.parameters}`
            );
            console.log(
              `Parameter code: ${param.parameter_code || param.code}`
            );
            console.log(`Parameter ID: ${param.parameter_id}`);
            console.log(`Full parameter object:`, param);

            // Check for ranges in different possible locations
            console.log(`\n--- RANGE SOURCE CHECK ---`);
            console.log(`param.ranges exists:`, Array.isArray(param.ranges));
            console.log(`param.ranges length:`, param.ranges?.length || 0);
            console.log(`param.ranges value:`, param.ranges);
            console.log(
              `param.allranges exists:`,
              Array.isArray(param.allranges)
            );
            console.log(
              `param.allranges length:`,
              param.allranges?.length || 0
            );
            console.log(`param.allranges value:`, param.allranges);

            // Check if ranges and allranges are the same reference or different
            if (Array.isArray(param.ranges) && Array.isArray(param.allranges)) {
              console.log(`WARNING: Both ranges and allranges exist!`);
              console.log(
                `ranges === allranges (same reference):`,
                param.ranges === param.allranges
              );
              console.log(
                `ranges length: ${param.ranges.length}, allranges length: ${param.allranges.length}`
              );

              // Check for duplicates within ranges array
              const rangeIds = param.ranges
                .map((r: any) => r.range_id)
                .filter((id: any) => id != null);
              const duplicateRangeIds = rangeIds.filter(
                (id: any, index: number) => rangeIds.indexOf(id) !== index
              );
              if (duplicateRangeIds.length > 0) {
                console.error(
                  `ERROR: Found duplicate range_ids in ranges array:`,
                  duplicateRangeIds
                );
              }

              // Check for duplicates by min/max/score combination
              const rangeSignatures = param.ranges.map(
                (r: any) => `${r.min}-${r.max}-${r.score}`
              );
              const duplicateSignatures = rangeSignatures.filter(
                (sig: string, index: number) =>
                  rangeSignatures.indexOf(sig) !== index
              );
              if (duplicateSignatures.length > 0) {
                console.error(
                  `ERROR: Found duplicate range signatures (min-max-score) in ranges array:`,
                  duplicateSignatures
                );
                console.error(
                  `Duplicate ranges:`,
                  param.ranges.filter((r: any, idx: number) =>
                    duplicateSignatures.includes(`${r.min}-${r.max}-${r.score}`)
                  )
                );
              }
            }

            // Ensure type is always a number
            let paramType = param.type || param.parameter_type;
            if (paramType == null || paramType === "") {
              paramType = 0;
            }
            // Convert to number if it's a string
            if (typeof paramType === "string") {
              paramType = parseInt(paramType, 10);
            }
            paramType = Number(paramType);

            // Use parameter_code first, then code (from saved data), then generate from gridCode
            const paramCode =
              param.parameter_code || param.code || this.gridCode + (idx + 1);

            // For raw or delta types, formula should be set to code
            let formulaValue = param.formula || param.parameter_formula || "";
            if (this.isRawOrDelta(paramType)) {
              formulaValue = paramCode;
            }

            // Determine which ranges to use - prioritize ranges over allranges
            let rangesToMap: any[] = [];
            if (Array.isArray(param.ranges) && param.ranges.length > 0) {
              console.log(`Using param.ranges (${param.ranges.length} ranges)`);
              rangesToMap = param.ranges;
            } else if (
              Array.isArray(param.allranges) &&
              param.allranges.length > 0
            ) {
              console.log(
                `Using param.allranges (${param.allranges.length} ranges)`
              );
              rangesToMap = param.allranges;
            } else {
              console.log(`No ranges found, using default ranges`);
            }

            console.log(
              `\n--- MAPPING RANGES (${rangesToMap.length} total) ---`
            );
            const mappedRanges =
              rangesToMap.length > 0
                ? rangesToMap.map((range: any, rangeIdx: number) => {
                    console.log(`  Mapping range ${rangeIdx + 1}:`, range);
                    // Check if this range is a duplicate of a previous one
                    const isDuplicate = rangesToMap
                      .slice(0, rangeIdx)
                      .some(
                        (prevRange: any) =>
                          prevRange.range_id &&
                          range.range_id &&
                          prevRange.range_id === range.range_id
                      );
                    if (isDuplicate) {
                      console.error(
                        `  ⚠️ DUPLICATE RANGE DETECTED at index ${rangeIdx}:`,
                        range
                      );
                    }

                    const mappedRange = {
                      score: Number(range.score) || 0,
                      min: Number(range.min) || Number(range.min_value) || 0,
                      max: Number(range.max) || Number(range.max_value) || 0,
                      selected:
                        range.selected !== undefined ? range.selected : false,
                      query: range.query || range.parameter_range_name || "",
                      range_id: range.range_id,
                      loadedFromSavedConfig:
                        range.loadedFromSavedConfig !== undefined
                          ? range.loadedFromSavedConfig
                          : range.range_id != null,
                    };
                    console.log(`  Mapped range ${rangeIdx + 1}:`, mappedRange);
                    return mappedRange;
                  })
                : [
                    { score: 0, min: 1, max: 20, selected: false, query: "" },
                    { score: 0, min: 21, max: 40, selected: false, query: "" },
                    { score: 0, min: 41, max: 60, selected: false, query: "" },
                    { score: 0, min: 61, max: 80, selected: false, query: "" },
                    { score: 0, min: 81, max: 100, selected: false, query: "" },
                  ];

            // Final duplicate check on mapped ranges
            const mappedRangeIds = mappedRanges
              .map((r: any) => r.range_id)
              .filter((id: any) => id != null);
            const duplicateMappedIds = mappedRangeIds.filter(
              (id: any, index: number) => mappedRangeIds.indexOf(id) !== index
            );
            if (duplicateMappedIds.length > 0) {
              console.error(
                `ERROR: Found duplicate range_ids in mapped ranges:`,
                duplicateMappedIds
              );
            }

            const mappedProduct = {
              parameters: param.parameter_name || param.parameters || "",
              unit: param.unit || param.parameter_unit || "",
              type: paramType,
              code: paramCode,
              uploadType: this.normalizeUploadType(
                param.uploadType,
                param.upload_source
              ),
              weight: param.weight || param.parameter_weight || 0,
              formula: formulaValue,
              calculation: param.calculation || param.parameter_calculation || "",
              criticalParameter: param.criticalParameter || false,
              parameter_id: param.parameter_id, // Preserve parameter_id from backend
              ranges: mappedRanges,
            };

            // Sort ranges by min value for proper display order
            mappedProduct.ranges.sort((a: RangeRow, b: RangeRow) => a.min - b.min);

            console.log(`\n=== FINAL MAPPED PRODUCT ${idx + 1} ===`);
            console.log(`Product: ${mappedProduct.parameters}`);
            console.log(`Total ranges: ${mappedProduct.ranges.length}`);
            console.log(`Ranges:`, mappedProduct.ranges);
            console.log(`=== END PARAMETER ${idx + 1} ===\n`);

            return mappedProduct;
          }
        );

        // Debug: Log the final products array to see what was created
        console.log("\n=== FINAL PRODUCTS ARRAY SUMMARY ===");
        console.log("Total products:", this.products.length);

        // Check for duplicate ranges across all products
        this.products.forEach((product, productIndex) => {
          console.log(
            `\n--- Product ${productIndex + 1}: ${product.parameters} ---`
          );
          console.log(
            `Code: ${product.code}, Parameter ID: ${product.parameter_id}`
          );
          console.log(`Total ranges: ${product.ranges?.length || 0}`);

          if (product.ranges && Array.isArray(product.ranges)) {
            // Check for duplicates within this product's ranges
            const rangeIds = product.ranges
              .map((r: any) => r.range_id)
              .filter((id: any) => id != null);
            const duplicateIds = rangeIds.filter(
              (id: any, index: number) => rangeIds.indexOf(id) !== index
            );

            if (duplicateIds.length > 0) {
              console.error(
                `⚠️ DUPLICATE RANGE IDs FOUND in Product ${productIndex + 1}:`,
                duplicateIds
              );
            }

            // Check for duplicate signatures (min-max-score)
            const signatures = product.ranges.map(
              (r: any) => `${r.min}-${r.max}-${r.score}`
            );
            const duplicateSigs = signatures.filter(
              (sig: string, index: number) => signatures.indexOf(sig) !== index
            );

            if (duplicateSigs.length > 0) {
              console.error(
                `⚠️ DUPLICATE RANGE SIGNATURES FOUND in Product ${
                  productIndex + 1
                }:`,
                duplicateSigs
              );
            }

            // Log each range
            product.ranges.forEach((range, rangeIndex) => {
              console.log(`  Range ${rangeIndex + 1}:`, {
                range_id: range.range_id,
                min: range.min,
                max: range.max,
                score: range.score,
                selected: range.selected,
                query: range.query?.substring(0, 20) + "..." || "",
                loadedFromSavedConfig: range.loadedFromSavedConfig,
              });
            });
          }
        });

        console.log("\n=== END FINAL PRODUCTS ARRAY SUMMARY ===\n");
      } else {
        console.log("WARNING: No allparameters found or not an array!");
        console.log("Module keys:", Object.keys(this.module || {}));
        console.log("allparameters value:", this.module?.allparameters);
        console.log("parameters value:", this.module?.parameters);
      }

      // Restore selectedProducts (checked rows)
      console.log("=== RESTORING SELECTED PRODUCTS ===");
      console.log(
        "Module selectedProductCodes:",
        this.module.selectedProductCodes
      );
      console.log("Products count:", this.products.length);
      console.log(
        "Products codes:",
        this.products.map((p) => p.code)
      );
      console.log(
        "Products parameter_ids:",
        this.products.map((p) => p.parameter_id)
      );

      if (
        Array.isArray(this.module.selectedProductCodes) &&
        this.module.selectedProductCodes.length > 0
      ) {
        // Filter products that match the selected codes
        this.selectedProducts = this.products.filter((p) =>
          this.module.selectedProductCodes.includes(p.code)
        );
        console.log(
          "Restored selectedProducts count:",
          this.selectedProducts.length
        );
        console.log(
          "Restored selectedProducts codes:",
          this.selectedProducts.map((p) => p.code)
        );

        // Debug: Check for any codes in selectedProductCodes that don't match any product
        const missingCodes = this.module.selectedProductCodes.filter(
          (code: string) => !this.products.some((p) => p.code === code)
        );
        if (missingCodes.length > 0) {
          console.warn(
            "Selected product codes not found in products:",
            missingCodes
          );
          console.warn(
            "This might indicate that some products were removed but their codes are still in selectedProductCodes"
          );
        }

        // Verify that all restored selectedProducts are actually in the products array
        const invalidSelected = this.selectedProducts.filter(
          (sp) => !this.products.includes(sp)
        );
        if (invalidSelected.length > 0) {
          console.error(
            "ERROR: Found selectedProducts that are not in products array:",
            invalidSelected
          );
          // Remove invalid selections
          this.selectedProducts = this.selectedProducts.filter((sp) =>
            this.products.includes(sp)
          );
        }
      } else if (Array.isArray(this.module.selectedProducts)) {
        // fallback for older saves
        this.selectedProducts = this.products.filter((p) =>
          this.module.selectedProducts.some((sp: any) => sp.code === p.code)
        );
        console.log(
          "Restored selectedProducts (fallback) count:",
          this.selectedProducts.length
        );
      } else {
        this.selectedProducts = [];
        console.log("No selectedProductCodes found, initializing empty array");
      }

      console.log(
        "Final selectedProducts count:",
        this.selectedProducts.length
      );
      console.log(
        "Final selectedProducts codes:",
        this.selectedProducts.map((p) => p.code)
      );
      console.log("=== END RESTORING SELECTED PRODUCTS ===");

      // Get module types from allmodules API response
      // Find the current module in allmodules to get its alltypes array
      let genericModule = null;
      if (data?.allmodules && Array.isArray(data.allmodules)) {
        // Try to find by module_id first (most reliable)
        if (this.module?.module_id) {
          genericModule = data.allmodules.find(
            (mod: any) =>
              mod.module_id == this.module.module_id ||
              mod.module_id === this.module.module_id
          );
        }

        // If not found by module_id, try by module_name
        if (!genericModule && (this.module?.module_name || this.module?.name)) {
          const moduleName = this.module.module_name || this.module.name;
          genericModule = data.allmodules.find(
            (mod: any) => mod.module_name === moduleName
          );
        }
      }

      // Extract module types from the generic module's alltypes array
      if (
        genericModule &&
        Array.isArray(genericModule.alltypes) &&
        genericModule.alltypes.length > 0
      ) {
        // Map alltypes to filteredModuleTypes format
        this.filteredModuleTypes = genericModule.alltypes
          .filter(
            (type: any) => type && type.type_name && type.type_status === "1"
          ) // Filter out inactive types
          .map((type: any) => ({
            type_name: type.type_name.trim(), // Trim whitespace from type names
            module_type_id: type.module_type_id,
            module_id: type.module_id,
          }));

        // Remove duplicates based on type_name
        this.filteredModuleTypes = this.filteredModuleTypes.filter(
          (type, index, self) =>
            index === self.findIndex((t) => t.type_name === type.type_name)
        );

        // Ensure the current module type is always included in the dropdown options
        // This prevents issues where the saved module type might not be in the alltypes list
        if (this.moduleType && this.moduleType.trim() !== "") {
          const currentTypeExists = this.filteredModuleTypes.some(
            (type) => type.type_name === this.moduleType || type.type_name.trim() === this.moduleType.trim()
          );
          if (!currentTypeExists) {
            console.log(`Adding current module type "${this.moduleType}" to dropdown options (was not in alltypes)`);
            this.filteredModuleTypes.unshift({ type_name: this.moduleType.trim() });
          }
        }

        console.log(
          `Found ${this.filteredModuleTypes.length} module types from alltypes for module: ${genericModule.module_name}`
        );
      } else {
        // Fallback if no alltypes found
        console.warn(
          "No alltypes found for current module, using fallback options"
        );
        const currentModuleType =
          this.module?.module_type || this.module?.moduleType;
        this.filteredModuleTypes = currentModuleType
          ? [{ type_name: currentModuleType }]
          : [{ type_name: "process" }, { type_name: "TPI" }];
      }

      // Debug: Log what we're getting for module types
      console.log(
        "Current module:",
        this.module?.module_name || this.module?.name
      );
      console.log(
        "Generic module found:",
        genericModule ? genericModule.module_name : "Not found"
      );
      console.log("Generic module alltypes:", genericModule?.alltypes);
      console.log("Filtered module types:", this.filteredModuleTypes);
      console.log("=== END DIALOG CONSTRUCTOR DEBUG ===");
    } catch (error) {
      console.error("Error in ModuleConfigDialogComponent constructor:", error);
      // Set default values if there's an error
      this.products = [];
      this.selectedProducts = [];
      this.filteredModuleTypes = [];
    }
  }

  /**
   * Helper function to get type label from type number
   * 0 = "raw", 1 = "score", 2 = "delta", 3 = "conditional"
   */
  getTypeLabel(type: number | string | any): string {
    // Convert to number if it's a string
    const typeNum = typeof type === "string" ? parseInt(type, 10) : type;

    if (typeNum === 0 || typeNum === "0") {
      return "RAW";
    } else if (typeNum === 1 || typeNum === "1") {
      return "SCORE";
    } else if (typeNum === 2 || typeNum === "2") {
      return "DELTA";
    } else if (typeNum === 3 || typeNum === "3") {
      return "CONDITIONAL";
    }

    // If type is already a string label (raw, score, delta, conditional), return it as is
    if (
      typeof type === "string" &&
      ["RAW", "SCORE", "DELTA", "CONDITIONAL"].includes(type.toUpperCase())
    ) {
      return type.toUpperCase();
    }

    // Default fallback - return the value as string or empty
    return type !== null && type !== undefined ? String(type) : "";
  }

  /**
   * Get available type options for dropdown
   */
  getTypeOptions(): Array<{ value: number; label: string }> {
    return [
      { value: 0, label: "RAW" },
      { value: 1, label: "SCORE" },
      { value: 2, label: "DELTA" },
      { value: 3, label: "CONDITIONAL" },
    ];
  }

  /**
   * Get available position numbers for dropdown
   * Returns array of numbers from 1 to configuredGridModulesCount
   */
  getPositionOptions(): number[] {
    if (this.configuredGridModulesCount <= 0) {
      return [];
    }
    return Array.from(
      { length: this.configuredGridModulesCount },
      (_, i) => i + 1
    );
  }

  /**
   * Check if type is raw or delta
   */
  isRawOrDelta(type: number | string | any): boolean {
    const typeNum = typeof type === "string" ? parseInt(type, 10) : type;
    return typeNum === 0 || typeNum === 2 || typeNum === "0" || typeNum === "2";
  }

  /**
   * Convert upload type from backend format to display format
   * Mapping: 0 = Select, 1 = Online, 2 = Manual
   * Handles numeric values (0/1/2) and string values
   */
  normalizeUploadType(uploadType: any, uploadSource?: any): string {
    // If already in correct format, return as is
    if (
      uploadType === "Manual" ||
      uploadType === "Online" ||
      uploadType === "Select"
    ) {
      return uploadType;
    }

    // Check upload_source first (0 = Select, 1 = Online, 2 = Manual)
    if (uploadSource !== undefined && uploadSource !== null) {
      const sourceNum =
        typeof uploadSource === "string"
          ? parseInt(uploadSource, 10)
          : uploadSource;
      if (!isNaN(sourceNum)) {
        if (sourceNum === 0) return "Select";
        if (sourceNum === 1) return "Online";
        if (sourceNum === 2) return "Manual";
      }
    }

    // Check uploadType as number or string
    if (uploadType !== undefined && uploadType !== null) {
      // Handle string values first
      if (typeof uploadType === "string") {
        const lowerType = uploadType.toLowerCase().trim();
        if (lowerType === "manual") return "Manual";
        if (lowerType === "online") return "Online";
        if (lowerType === "select") return "Select";
        // Handle string numbers: "0" = Select, "1" = Online, "2" = Manual
        if (lowerType === "0") return "Select";
        if (lowerType === "1") return "Online";
        if (lowerType === "2") return "Manual";
      }

      // Handle numeric values
      const typeNum =
        typeof uploadType === "string" ? parseInt(uploadType, 10) : uploadType;
      if (!isNaN(typeNum)) {
        // Mapping:
        // 0 = Select
        // 1 = Online
        // 2 = Manual
        if (typeNum === 0) return "Select";
        if (typeNum === 1) return "Online";
        if (typeNum === 2) return "Manual";
      }
    }

    // Default fallback
    return "";
  }

  ngOnInit() {
    try {
      // Module types are already loaded in constructor from alltypes
      // This ensures they're available if constructor didn't complete properly
      if (!this.filteredModuleTypes || this.filteredModuleTypes.length === 0) {
        // Fallback: Try to get from allmodules again
        let genericModule = null;
        if (this.data?.allmodules && Array.isArray(this.data.allmodules)) {
          if (this.module?.module_id) {
            genericModule = this.data.allmodules.find(
              (mod: any) =>
                mod.module_id == this.module.module_id ||
                mod.module_id === this.module.module_id
            );
          }

          if (
            !genericModule &&
            (this.module?.module_name || this.module?.name)
          ) {
            const moduleName = this.module.module_name || this.module.name;
            genericModule = this.data.allmodules.find(
              (mod: any) => mod.module_name === moduleName
            );
          }

          if (
            genericModule &&
            Array.isArray(genericModule.alltypes) &&
            genericModule.alltypes.length > 0
          ) {
            this.filteredModuleTypes = genericModule.alltypes
              .filter(
                (type: any) =>
                  type && type.type_name && type.type_status === "1"
              )
              .map((type: any) => ({
                type_name: type.type_name.trim(),
                module_type_id: type.module_type_id,
                module_id: type.module_id,
              }));

            this.filteredModuleTypes = this.filteredModuleTypes.filter(
              (type, index, self) =>
                index === self.findIndex((t) => t.type_name === type.type_name)
            );
          }
        }
      }

      // Fix range selection state after component is initialized
      setTimeout(() => {
        try {
          this.fixRangeSelectionState();
        } catch (error) {
          console.warn("Error in range selection fix:", error);
        }
      }, 100);
    } catch (error) {
      console.error("Error in ngOnInit:", error);
    }
  }

  ngAfterViewInit() {
    // Add a delay and null check to prevent focus errors
    setTimeout(() => {
      try {
        if (
          this.aliasInput &&
          this.aliasInput.nativeElement &&
          !this.hideHeaderFields
        ) {
          this.aliasInput.nativeElement.focus();
        }
      } catch (error) {
        console.warn("Could not focus alias input:", error);
      }
    }, 100);
  }

  /**
   * Test method to manually verify range selection state
   */
  public testRangeSelection(): void {
    try {
      console.log("=== TESTING RANGE SELECTION STATE ===");
      this.products.forEach((product, productIndex) => {
        console.log(`Product ${productIndex}: ${product.parameters}`);
        if (product.ranges && Array.isArray(product.ranges)) {
          product.ranges.forEach((range, rangeIndex) => {
            console.log(
              `  Range ${rangeIndex}: selected=${range.selected}, loadedFromSavedConfig=${range.loadedFromSavedConfig}`
            );
          });
        }
      });
      console.log("=== END TEST ===");
    } catch (error) {
      console.error("Error in testRangeSelection:", error);
    }
  }

  /**
   * Force all ranges to be selected (for testing purposes)
   */
  public forceSelectAllRanges(): void {
    try {
      console.log("=== FORCING ALL RANGES TO BE SELECTED ===");
      this.products.forEach((product, productIndex) => {
        if (product.ranges && Array.isArray(product.ranges)) {
          product.ranges.forEach((range, rangeIndex) => {
            console.log(
              `Forcing range ${rangeIndex} to be selected for product ${productIndex}`
            );
            range.selected = true;
          });
        }
      });

      // Log the final state
      console.log("Final state after forcing selection:");
      this.products.forEach((product, productIndex) => {
        if (product.ranges && Array.isArray(product.ranges)) {
          product.ranges.forEach((range, rangeIndex) => {
            console.log(
              `Product ${productIndex}, Range ${rangeIndex} final state:`,
              {
                selected: range.selected,
                loadedFromSavedConfig: range.loadedFromSavedConfig,
              }
            );
          });
        }
      });

      console.log("=== FORCE SELECTION COMPLETED ===");
    } catch (error) {
      console.error("Error in forceSelectAllRanges:", error);
    }
  }

  /**
   * Create a test product with ranges to verify UI binding
   */
  public createTestProduct(): void {
    try {
      console.log("=== CREATING TEST PRODUCT ===");

      const testProduct: Product = {
        parameters: "Test Parameter",
        unit: "test",
        type: 0,
        code: "TEST001",
        uploadType: "Manual",
        weight: 10,
        formula: "test",
        calculation: "",
        criticalParameter: false,
        ranges: [
          { score: 0, min: 1, max: 20, selected: true, query: "test1" },
          { score: 0, min: 21, max: 40, selected: true, query: "test2" },
          { score: 0, min: 41, max: 60, selected: false, query: "test3" },
        ],
      };

      this.products.unshift(testProduct);
      try {
        this.cdr.detectChanges();
      } catch (error) {
        console.warn(
          "Error in change detection after adding test product:",
          error
        );
      }

      console.log("Test product added:", testProduct);
      console.log("Updated products array:", this.products);
    } catch (error) {
      console.error("Error in createTestProduct:", error);
    }
  }

  /**
   * Fix range selection state to ensure checkboxes are properly checked
   * This method handles cases where ranges loaded from backend might not have proper selection state
   */
  private fixRangeSelectionState(): void {
    try {
      console.log("Fixing range selection state...");

      this.products.forEach((product, productIndex) => {
        if (product.ranges && Array.isArray(product.ranges)) {
          console.log(
            `Product ${productIndex} has ${product.ranges.length} ranges:`,
            product.ranges
          );
          product.ranges.forEach((range, rangeIndex) => {
            console.log(`Range ${rangeIndex} before fix:`, {
              selected: range.selected,
              loadedFromSavedConfig: range.loadedFromSavedConfig,
            });
            // If range has loadedFromSavedConfig flag, it should definitely be selected
            if (range.loadedFromSavedConfig && !range.selected) {
              console.log(
                `Fixing range selection for product ${productIndex}, range ${rangeIndex}:`,
                range
              );
              range.selected = true;
            }
            console.log(`Range ${rangeIndex} after fix:`, {
              selected: range.selected,
              loadedFromSavedConfig: range.loadedFromSavedConfig,
            });
          });
        }
      });

      // Additional debugging: Check final state
      console.log("Final state after fixRangeSelectionState:");
      this.products.forEach((product, productIndex) => {
        if (product.ranges && Array.isArray(product.ranges)) {
          product.ranges.forEach((range, rangeIndex) => {
            console.log(
              `Product ${productIndex}, Range ${rangeIndex} final state:`,
              {
                selected: range.selected,
                loadedFromSavedConfig: range.loadedFromSavedConfig,
                range: range,
              }
            );
          });
        }
      });
    } catch (error) {
      console.error("Error in fixRangeSelectionState:", error);
    }
  }

  close() {
    // Clear weight validation toasts before closing
    this.messageService.clear("weightValidation");
    this.dialogRef.close();
  }

  isFormValid(): boolean {
    // Check if form has alias and type fields (only validate if hideHeaderFields is false)
    if (!this.hideHeaderFields) {
      // Check if alias name is filled (trim whitespace and check if not empty)
      if (!this.aliasName || this.aliasName.trim() === "") {
        return false;
      }

      // Check if module type is selected
      if (!this.moduleType || this.moduleType.trim() === "") {
        return false;
      }
    }

    // Check if at least one product is selected
    if (!this.selectedProducts || this.selectedProducts.length === 0) {
      return false;
    }

    // Check if at least one range is selected across all products
    const hasAnyRangeSelected = this.products.some(
      (product) =>
        product.ranges && product.ranges.some((range) => range.selected)
    );
    if (!hasAnyRangeSelected) {
      return false;
    }

    // Check if total weight equals 100
    if (!this.isTotalWeightValid()) {
      return false;
    }

    // Check if any parameter exceeds 60 characters
    if (this.hasParameterExceedingLimit()) {
      return false;
    }

    return true;
  }

  /**
   * Checks if any product's parameters field exceeds 60 characters
   */
  hasParameterExceedingLimit(): boolean {
    if (!this.products) {
      return false;
    }
    return this.products.some(
      (product) => product.parameters && product.parameters.length > 100
    );
  }

  /**
   * Checks if a specific product's parameters field exceeds 60 characters
   */
  isParameterExceedingLimit(product: Product): boolean {
    return product.parameters && product.parameters.length > 100;
  }

  save() {
    if (!this.isFormValid()) {
      // Show weight validation toast if validation fails
      this.showWeightValidationToast();
      return;
    }

    // Validate ranges for all products
    for (const product of this.products) {
      if (!this.validateRanges(product)) {
        return; // Stop saving if validation fails
      }
    }

    // Save input fields
    console.log("=== SAVING DIALOG FIELDS ===");
    console.log("Saving aliasName:", this.aliasName);
    console.log("Saving moduleType:", this.moduleType);
    console.log("Saving positionNo:", this.positionNo);
    console.log("Saving displayOnDashboard:", this.displayOnDashboard);

    this.module.aliasName = this.aliasName;
    this.module.moduleType = this.moduleType;
    this.module.positionNo = this.positionNo;
    this.module.position_no = this.positionNo; // Also save in snake_case for consistency
    this.module.porder = this.positionNo; // Save as porder for payload
    this.module.displayOnDashboard = this.displayOnDashboard;

    console.log("Module after saving fields:", {
      aliasName: this.module.aliasName,
      moduleType: this.module.moduleType,
      positionNo: this.module.positionNo,
      porder: this.module.porder,
      displayOnDashboard: this.module.displayOnDashboard,
    });

    // Save selected rows (by code for robustness)
    // Filter selectedProducts to only include products that are still in the products array
    // This ensures consistency and prevents orphaned references
    const validSelectedProducts = this.selectedProducts.filter((sp) =>
      this.products.some((p) => p.code === sp.code)
    );
    this.module.selectedProductCodes = validSelectedProducts.map((p) => p.code);

    console.log("=== SAVING SELECTED PRODUCTS ===");
    console.log(
      "Selected products count (before filter):",
      this.selectedProducts.length
    );
    console.log(
      "Valid selected products count (after filter):",
      validSelectedProducts.length
    );
    console.log(
      "Selected product codes being saved:",
      this.module.selectedProductCodes
    );
    console.log("Products count:", this.products.length);
    console.log(
      "Products codes:",
      this.products.map((p) => p.code)
    );
    console.log("=== END SAVING SELECTED PRODUCTS ===");

    // Save the parameters as allparameters for persistence
    // Note: Only selected ranges are sent to backend to reduce payload size
      this.module.allparameters = this.products.map((product) => ({
        parameters: product.parameters,
        parameter_name: product.parameters,
        parameter_unit: product.unit,
        parameter_type: product.type,
        code: product.code,
        parameter_code: product.code, // Also save as parameter_code for consistency
        uploadType: product.uploadType,
        weight: product.weight,
        formula: product.formula,
        calculation: product.calculation || "",
        parameter_calculation: product.calculation || "",
        criticalParameter: product.criticalParameter,
        parameter_id: product.parameter_id, // Preserve parameter_id for backend
        allranges: product.ranges.map((range, index) => ({
        score: range.score,
        min: range.min,
        max: range.max,
        selected: range.selected,
        query: range.query,
        range_name: `range${index + 1}`, // Add range_name field (range1, range2, etc.)
      })),
      ranges: product.ranges, // Also save ranges for dialog restoration
    }));

    this.dialogRef.close(this.module);

    // Log to verify only selected ranges are being sent
    console.log("Submitting to backend:", this.module);
    console.log("Selected ranges being sent:");
    this.module.allparameters.forEach((param: any, idx: number) => {
      console.log(`Parameter ${idx + 1} (${param.parameter_name}):`, {
        totalRanges: param.allranges?.length || 0,
        ranges: param.allranges?.map((r: any) => ({
          min: r.min,
          max: r.max,
          selected: r.selected,
          score: r.score,
        })),
      });
    });
  }

  // Helper to get default min/max for a given range index
  getDefaultRangeMinMax(index: number) {
    if (index === 0) {
      // First range: 1-20
      return {
        min: 1,
        max: 20,
      };
    } else {
      // Subsequent ranges: start where previous ended (inclusive boundary)
      const step = 20;
      const min = index * step;
      const max = min + 20;
      return {
        min: min,
        max: max,
      };
    }
  }

  // Handler for checkbox change
  onRangeSelect(product: Product, rangeIndex: number) {
    try {
      const range = product.ranges[rangeIndex];

      // Prevent unselecting configured ranges unless in edit mode
      if (
        range.loadedFromSavedConfig &&
        range.selected &&
        this.editingProduct !== product
      ) {
        // Don't allow unselecting configured ranges when not in edit mode
        return;
      }

      // Toggle the selected state since we're using [checked] instead of [(ngModel)]
      range.selected = !range.selected;

      if (range.selected) {
        // Set default min/max when checked
        const def = this.getDefaultRangeMinMax(rangeIndex);
        range.min = def.min;
        range.max = def.max;
      }
      // If unchecked, you can optionally clear min/max or leave as is

      // Validate ranges after selection change
      this.validateRanges(product);

      // Force change detection to update the UI
      try {
        this.cdr.detectChanges();
      } catch (error) {
        console.warn("Error in change detection after range selection:", error);
      }
    } catch (error) {
      console.error("Error in onRangeSelect:", error);
    }
  }

  onRangeValueChange(
    product: Product,
    rangeIndex: number,
    field: "min" | "max"
  ) {
    const range = product.ranges[rangeIndex];

    // Clear validation error flag when user makes changes
    if (range.hasValidationError) {
      range.hasValidationError = false;
      // Re-validate the product to update validation state
      this.validateRanges(product);
    } else {
      // Validate immediately when user changes values (but don't auto-adjust values)
      this.validateRanges(product);
    }

    // Values are now static and independent - no automatic adjustments
    // User can set their own min/max values as needed
  }

  // Remove the complex adjacent range adjustment method
  // adjustAdjacentRangesForContiguity(product: Product, currentIndex: number) { ... }

  adjustAdjacentRanges(product: Product, currentIndex: number) {
    const ranges = product.ranges;
    if (ranges.length <= 1) return;

    // Adjust previous range if it exists
    if (currentIndex > 0) {
      const current = ranges[currentIndex];
      const previous = ranges[currentIndex - 1];
      if (previous.max >= current.min) {
        previous.max = current.min - 1;
      }
    }

    // Adjust next range if it exists
    if (currentIndex < ranges.length - 1) {
      const current = ranges[currentIndex];
      const next = ranges[currentIndex + 1];
      if (next.min <= current.max) {
        next.min = current.max + 1;
      }
    }
  }

  toggleProductSelection(product: Product) {
    if (!this.selectedProducts) this.selectedProducts = [];
    const idx = this.selectedProducts.indexOf(product);
    const isUnselecting = idx > -1;
    const productWeight = product.weight || 0;

    if (isUnselecting) {
      // Unselecting: Remove from selectedProducts and also remove from products array (configured parameter table)
      this.selectedProducts.splice(idx, 1);

      // Find and remove the product from the products array
      const productIdx = this.products.indexOf(product);
      if (productIdx > -1) {
        this.products.splice(productIdx, 1);
      }

      // If the product was being edited, cancel editing
      if (this.editingProduct === product) {
        this.editingProduct = null;
        this.originalProduct = null;
      }

      // Show weight validation toast only if the unselected parameter has weight not equal to 0
      if (productWeight !== 0) {
        setTimeout(() => {
          this.showWeightValidationToast();
        }, 100);
      } else {
        // Clear validation toast if unselected parameter has weight 0
        setTimeout(() => {
          this.messageService.clear("weightValidation");
        }, 100);
      }
    } else {
      // Selecting: Add to selectedProducts (product is already in products array)
      this.selectedProducts.push(product);

      // Show weight validation toast after selection
      setTimeout(() => {
        this.showWeightValidationToast();
      }, 100);
    }
  }

  /**
   * Check if a parameter is already configured in the products array
   * Used in the add parameter dialog to show which parameters are already configured
   */
  isParameterAlreadyConfigured(product: Product): boolean {
    if (!product) return false;

    // Check by code first
    const existsByCode = this.products.some((p) => p.code === product.code);
    if (existsByCode) return true;

    // Also check by parameter_id if available
    if (product.parameter_id) {
      const existsById = this.products.some(
        (p) => p.parameter_id && p.parameter_id === product.parameter_id
      );
      if (existsById) return true;
    }

    return false;
  }

  openEditor(range: any) {
    this.editorRange = range;
    this.editorText = range.query || "";
    this.editorVisible = true;
  }

  saveEditor() {
    if (this.editorRange) {
      this.editorRange.query = this.editorText;
    }
    this.editorVisible = false;
    this.editorRange = null;
  }

  onGlobalSearch(value: string) {
    try {
      // Example: Only allow alphanumeric and spaces, max 50 chars
      const valid = /^[\w\s-]*$/.test(value);
      if (!valid) {
        this.searchError =
          "Only letters, numbers, spaces, and hyphens are allowed.";
        return;
      }
      if (value.length > 50) {
        this.searchError = "Search is too long (max 50 characters).";
        return;
      }
      this.searchError = "";
      if (this.dt && typeof this.dt.filterGlobal === "function") {
        this.dt.filterGlobal(value, "contains");
      }
    } catch (error) {
      console.warn("Error in global search:", error);
    }
  }
  openFormulaEditor(product: any) {
    this.formulaEditorProduct = product;
    this.formulaEditorText = product.formula || "";
    this.formulaEditorVisible = true;
  }

  saveFormulaEditor() {
    if (this.formulaEditorProduct) {
      // For raw/delta types, formula should always be set to code
      if (this.isRawOrDelta(this.formulaEditorProduct.type)) {
        this.formulaEditorProduct.formula =
          this.formulaEditorProduct.code || "";
      } else {
        this.formulaEditorProduct.formula = this.formulaEditorText;
      }
    }
    this.formulaEditorVisible = false;
    this.formulaEditorProduct = null;
  }

  startEdit(product: any) {
    this.editingProduct = product;
    // Deep clone to compare later
    this.originalProduct = JSON.parse(JSON.stringify(product));

    // Ensure type is always a number when editing starts
    if (typeof product.type === "string") {
      product.type = parseInt(product.type, 10);
    }
    product.type = Number(product.type);

    console.log("startEdit - normalized type:", {
      code: product.code,
      type: product.type,
      typeType: typeof product.type,
    });

    // If type is raw or delta, set formula to code value
      if (this.isRawOrDelta(product.type)) {
        product.formula = product.code || "";
      }
    }

  toggleCalculationSelection(product: Product, option: "DMS" | "PUREBI") {
    if (!product) {
      return;
    }
    product.calculation = product.calculation === option ? "" : option;
  }

  /**
   * Handle type change from ngModelChange event - update formula if type is raw or delta
   */
  onTypeChangeFromEvent(product: any, newTypeValue: any) {
    console.log("onTypeChangeFromEvent called:", {
      productCode: product.code,
      oldType: product.type,
      oldTypeType: typeof product.type,
      newTypeFromEvent: newTypeValue,
      newTypeType: typeof newTypeValue,
    });

    // Ensure newType is a number - handle both string and number inputs
    let typeValue: number;
    if (typeof newTypeValue === "string") {
      typeValue = parseInt(newTypeValue, 10);
    } else if (typeof newTypeValue === "number") {
      typeValue = newTypeValue;
    } else if (newTypeValue == null || isNaN(Number(newTypeValue))) {
      console.warn("Invalid type value:", newTypeValue);
      return;
    } else {
      typeValue = Number(newTypeValue);
    }

    // Ensure we have a valid number
    if (isNaN(typeValue)) {
      console.warn("Type value is NaN:", newTypeValue);
      return;
    }

    // Update product.type immediately - this is critical for the dropdown to show the correct value
    product.type = typeValue;
    console.log(
      "Updated product.type to:",
      product.type,
      "type:",
      typeof product.type
    );

    // Update formula if type is raw or delta
    if (this.isRawOrDelta(typeValue)) {
      product.formula = product.code || "";
      console.log("Updated formula to code:", product.formula);
    }

    // Force change detection immediately
    this.cdr.markForCheck();
    this.cdr.detectChanges();
    console.log("Change detection triggered");
  }

  /**
   * Handle code change - update formula if type is raw or delta
   */
  onCodeChange(product: any) {
    if (this.isRawOrDelta(product.type)) {
      product.formula = product.code || "";
    }
  }

  removeRange(product: any, rangeIndex: number) {
    if (product.ranges && product.ranges.length > 1) {
      // Show confirmation dialog
      if (confirm(`Are you sure you want to remove Range ${rangeIndex + 1}?`)) {
        product.ranges.splice(rangeIndex, 1);
        // Recalculate range numbers and adjust min/max values if needed
        this.recalculateRanges(product);
      }
    }
  }

  recalculateRanges(product: any) {
    if (!product.ranges || product.ranges.length === 0) return;

    // Sort ranges by min value to ensure proper order
    product.ranges.sort((a: RangeRow, b: RangeRow) => a.min - b.min);

    // Basic validation: ensure each range has min < max
    for (let i = 0; i < product.ranges.length; i++) {
      const current = product.ranges[i];

      // If range has invalid values, set some default reasonable values
      if (current.min >= current.max) {
        current.min = i * 20 + 1;
        current.max = (i + 1) * 20;
      }
    }
  }

  addNewRange(product: any) {
    if (!product.ranges) {
      product.ranges = [];
    }

    // Simply create a new range with empty values
    // User will fill in the min/max values manually
    const newRange: RangeRow = {
      score: 0,
      min: 0, // Empty value, user will fill
      max: 0, // Empty value, user will fill
      selected: false,
      query: "",
    };

    // Add the new range
    product.ranges.push(newRange);

    // Show success message
    this.messageService.add({
      severity: "success",
      summary: "Range Added",
      detail: `Range ${product.ranges.length} has been added successfully. Please fill in the min/max values manually.`,
      life: 3000,
    });
  }

  // Remove the validateNewRange method as it's no longer needed
  // validateNewRange(ranges: RangeRow[], newMin: number, newMax: number): { isValid: boolean; message: string } { ... }

  checkRangeOverlap(
    ranges: RangeRow[],
    newMin: number,
    newMax: number
  ): boolean {
    return ranges.some((range) => {
      // Check if new range overlaps with existing range
      return newMin <= range.max && newMax >= range.min;
    });
  }

  validateRanges(product: any): boolean {
    if (!product.ranges || product.ranges.length === 0) return true;

    // Clear previous validation errors
    product.ranges.forEach((range: any) => {
      range.hasValidationError = false;
    });

    let hasError = false;
    const selectedRanges = product.ranges.filter((r: any) => r.selected);

    // Sort selected ranges by min value to ensure proper order
    const sortedSelectedRanges = [...selectedRanges].sort(
      (a: any, b: any) => a.min - b.min
    );

    // Validate each selected range
    for (let i = 0; i < sortedSelectedRanges.length; i++) {
      const range = sortedSelectedRanges[i];
      const originalIndex = product.ranges.indexOf(range);

      // Check if range has valid min/max values
      if (range.min >= range.max) {
        range.hasValidationError = true;
        hasError = true;
        this.messageService.add({
          severity: "error",
          summary: "Range Validation Error",
          detail: `Range ${originalIndex + 1} has invalid values: min (${
            range.min
          }) must be less than max (${range.max}).`,
          life: 5000,
        });
        continue;
      }

      // Check if range has reasonable values (not negative)
      if (range.min < 0 || range.max < 0) {
        range.hasValidationError = true;
        hasError = true;
        this.messageService.add({
          severity: "error",
          summary: "Range Validation Error",
          detail: `Range ${originalIndex + 1} has negative values: min (${
            range.min
          }), max (${range.max}). Values must be positive.`,
          life: 5000,
        });
        continue;
      }

      // Check continuity: For ranges after the first one, the previous range's max should equal current range's min
      if (i > 0) {
        const previousRange = sortedSelectedRanges[i - 1];
        if (previousRange.max !== range.min) {
          range.hasValidationError = true;
          previousRange.hasValidationError = true;
          hasError = true;
          const previousOriginalIndex = product.ranges.indexOf(previousRange);
          this.messageService.add({
            severity: "error",
            summary: "Range Continuity Error",
            detail: `Range ${previousOriginalIndex + 1} max (${
              previousRange.max
            }) must equal Range ${originalIndex + 1} min (${
              range.min
            }). Ranges must be continuous.`,
            life: 5000,
          });
        }
      }
    }

    if (hasError) {
      return false;
    }

    return true;
  }

  saveEdit(product: any) {
    // Validate ranges before saving
    if (!this.validateRanges(product)) {
      // Validation failed, ranges are highlighted, don't save
      return;
    }

    this.editingProduct = null;
    this.originalProduct = null;
  }

  cancelEdit(product: any) {
    if (this.originalProduct) {
      const restored = JSON.parse(JSON.stringify(this.originalProduct));
      Object.keys(product).forEach((key) => {
        delete product[key];
      });
      Object.assign(product, restored);
    }
    this.editingProduct = null;
    this.originalProduct = null;
  }

  hasProductChanged(product: any): boolean {
    return JSON.stringify(product) !== JSON.stringify(this.originalProduct);
  }

  // Old methods removed - replaced with new parameter selection functionality

  openFormulaEditorDialog(product: any) {
    setTimeout(() => {
      this.formulaEditorDialogProduct = product;
      this.formulaEditorDialogText = product.formula || "";
      this.formulaEditorDialogVisible = true;
    }, 0);
  }
  closeFormulaEditorDialog() {
    this.formulaEditorDialogVisible = false;
    this.formulaEditorDialogProduct = null;
    this.formulaEditorDialogText = "";
  }

  saveFormulaEditorDialog() {
    if (this.formulaEditorDialogProduct) {
      // For raw/delta types, formula should always be set to code
      if (this.isRawOrDelta(this.formulaEditorDialogProduct.type)) {
        this.formulaEditorDialogProduct.formula =
          this.formulaEditorDialogProduct.code || "";
      } else {
        this.formulaEditorDialogProduct.formula = this.formulaEditorDialogText;
      }
    }
    this.closeFormulaEditorDialog();
  }

  showFormulaToast() {
    this.messageService.add({
      severity: "info",
      summary: "Info",
      detail: "Edit formula in configure section",
      life: 3000,
    });
  }
  showQueryToast() {
    this.messageService.add({
      severity: "info",
      summary: "Info",
      detail: "Edit query in configure section",
      life: 3000,
    });
  }

  // Calculate total weight of selected parameters
  calculateTotalWeight(): number {
    if (!this.selectedProducts || this.selectedProducts.length === 0) {
      return 0;
    }

    return this.selectedProducts.reduce((total, product) => {
      const weight = product.weight || 0;
      return total + weight;
    }, 0);
  }

  // Check if total weight equals 100
  isTotalWeightValid(): boolean {
    const totalWeight = this.calculateTotalWeight();
    return totalWeight === 100;
  }

  // Get weight validation message
  getWeightValidationMessage(): string {
    const totalWeight = this.calculateTotalWeight();
    if (totalWeight === 0) {
      return "Please select parameters first";
    } else if (totalWeight < 100) {
      return `Total weight is ${totalWeight}. Remember total weight should be 100, Not less than that.`;
    } else if (totalWeight > 100) {
      return `Total weight is ${totalWeight}. Remember total weight should be 100, Not more than that.`;
    }
    return "weightage validated";
  }

  // Handle weight changes to update validation
  onWeightChange(product: any) {
    // Prevent negative weight values
    if (product.weight < 0) {
      product.weight = 0;
    }

    // Ensure weight is a valid number
    if (
      isNaN(product.weight) ||
      product.weight === null ||
      product.weight === undefined
    ) {
      product.weight = 0;
    }

    // Trigger change detection for validation
    this.cdr.detectChanges();

    // Show weight validation toast
    this.showWeightValidationToast();
  }

  // Check if alias name is valid
  isAliasNameValid(): boolean {
    return (
      this.hideHeaderFields || (this.aliasName && this.aliasName.trim() !== "")
    );
  }

  // Check if module type is valid
  isModuleTypeValid(): boolean {
    return (
      this.hideHeaderFields ||
      (this.moduleType && this.moduleType.trim() !== "")
    );
  }

  // Get validation message for header fields
  getHeaderFieldsValidationMessage(): string {
    if (this.hideHeaderFields) {
      return "";
    }

    const messages: string[] = [];

    if (!this.isAliasNameValid()) {
      messages.push("Module Alias Name");
    }

    if (!this.isModuleTypeValid()) {
      messages.push("Module Type");
    }

    if (messages.length > 0) {
      return `Please fill the field value of ${messages.join(" and ")}`;
    }

    return "";
  }

  // Show weight validation toast
  showWeightValidationToast() {
    const totalWeight = this.calculateTotalWeight();

    if (totalWeight === 0) {
      // Don't show toast when no parameters are selected
      return;
    } else if (totalWeight < 100) {
      this.messageService.add({
        severity: "warn",
        summary: "Weight Validation",
        detail: `Total weight is ${totalWeight}. Remember total weight should be 100, Not less than that.`,
        life: 0,
        key: "weightValidation",
      });
    } else if (totalWeight > 100) {
      this.messageService.add({
        severity: "error",
        summary: "Weight Validation",
        detail: `Total weight is ${totalWeight}. Remember total weight should be 100, Not more than that.`,
        life: 0,
        key: "weightValidation",
      });
    } else {
      // Total weight = 100, show success and clear other toasts
      this.messageService.clear("weightValidation");
      this.messageService.add({
        severity: "success",
        summary: "Weight Validation",
        detail: "weightage validated",
        life: 3000,
        key: "weightValidation",
      });
    }
  }

  openAddParameterDialog() {
    console.log("=== OPENING ADD PARAMETER DIALOG ===");
    console.log("Current module:", this.module);
    console.log("Available allmodules:", this.data?.allmodules);

    // Find the generic module data for the current module
    let genericModule = null;
    if (this.data?.allmodules && Array.isArray(this.data.allmodules)) {
      // Try to find by module_id first
      if (this.module?.module_id) {
        genericModule = this.data.allmodules.find(
          (mod: any) => mod.module_id == this.module.module_id
        );
      }

      // If not found by module_id, try by module_name
      if (!genericModule && this.module?.module_name) {
        genericModule = this.data.allmodules.find(
          (mod: any) => mod.module_name === this.module.module_name
        );
      }

      console.log("Found generic module:", genericModule);
    }

    if (genericModule && Array.isArray(genericModule.allparameters)) {
      console.log("Generic module parameters:", genericModule.allparameters);

      // Get existing product codes to check which parameters are already configured
      const existingProductCodes = new Set(this.products.map((p) => p.code));
      // Normalize parameter IDs to strings for consistent comparison
      const existingParameterIds = new Set(
        this.products
          .map((p) => p.parameter_id)
          .filter((id) => id !== null && id !== undefined)
          .map((id) => String(id)) // Convert to string for consistent comparison
      );

      console.log("=== CONFIGURED PARAMETERS INFO ===");
      console.log("Configured parameters count:", this.products.length);
      console.log(
        "Configured parameters:",
        this.products.map((p) => ({
          code: p.code,
          parameter_id: p.parameter_id,
          name: p.parameters,
          type: p.type,
          weight: p.weight,
        }))
      );
      console.log("Existing product codes:", Array.from(existingProductCodes));
      console.log("Existing parameter IDs:", Array.from(existingParameterIds));
      console.log("=== END CONFIGURED PARAMETERS INFO ===");

      // Transform generic library parameters to the format expected by the dialog
      // Track seen parameter IDs to prevent duplicates within generic library
      const seenParameterIds = new Set<string>();
      // Track which configured parameters have already been matched to prevent showing multiple generic params for same configured param
      const matchedConfiguredParams = new Set<string>(); // Store as "code:parameter_id" or just "code"
      const totalGenericParams = genericModule.allparameters.length;
      let duplicateCount = 0;

      this.addParameterProducts = genericModule.allparameters
        .filter((param: any) => {
          // Normalize parameter_id to string for comparison
          const normalizedParamId = param.parameter_id
            ? String(param.parameter_id)
            : null;

          // Filter out duplicates by parameter_id within generic library only
          // (Allow parameters that match configured ones - they'll be disabled)
          if (normalizedParamId && seenParameterIds.has(normalizedParamId)) {
            duplicateCount++;
            console.log(
              `Skipping duplicate generic parameter with parameter_id: ${normalizedParamId}`
            );
            return false;
          }

          // Mark this parameter_id as seen
          if (normalizedParamId) {
            seenParameterIds.add(normalizedParamId);
          }

          return true;
        })
        .map((param: any, idx: number) => {
          const paramCode = param.parameter_code || this.gridCode + (idx + 1);

          // Normalize parameter_id to string for comparison
          const normalizedParamId = param.parameter_id
            ? String(param.parameter_id)
            : null;

          // Check if this parameter matches any configured parameter
          // A parameter is considered already configured if it matches by code OR parameter_id
          // But we need to find the specific configured parameter that matches
          let existingProduct: Product | null = null;
          let matchReason = "";

          // First try to find by code (most reliable)
          if (existingProductCodes.has(paramCode)) {
            existingProduct =
              this.products.find((p) => p.code === paramCode) || null;
            if (existingProduct) {
              matchReason = `code (${paramCode})`;
            }
          }

          // If not found by code, try by parameter_id
          if (
            !existingProduct &&
            normalizedParamId &&
            existingParameterIds.has(normalizedParamId)
          ) {
            existingProduct =
              this.products.find(
                (p) =>
                  p.parameter_id && String(p.parameter_id) === normalizedParamId
              ) || null;
            if (existingProduct) {
              matchReason = `parameter_id (${normalizedParamId})`;
            }
          }

          // Parameter is already configured if we found a matching configured parameter
          const isAlreadyConfigured = existingProduct !== null;

          // If this generic parameter matches a configured parameter, check if we've already shown another generic param for this configured param
          if (isAlreadyConfigured && existingProduct) {
            const configuredParamKey = `${existingProduct.code}:${existingProduct.parameter_id}`;

            // If we've already matched this configured parameter with another generic parameter, skip this one
            if (matchedConfiguredParams.has(configuredParamKey)) {
              duplicateCount++;
              console.log(
                `Skipping duplicate match for configured parameter: ${
                  existingProduct.parameters
                } (${existingProduct.code}) - Generic param: ${
                  param.parameter_name || param.parameters || "Unknown"
                } (Code: ${paramCode}, ID: ${normalizedParamId})`
              );
              return null; // Return null to filter it out later
            }

            // Mark this configured parameter as matched
            matchedConfiguredParams.add(configuredParamKey);

            // Log matching details for debugging
            console.log(
              `Parameter matched as configured: ${
                param.parameter_name || param.parameters || "Unknown"
              } - Code: ${paramCode}, Parameter ID: ${normalizedParamId}, Match reason: ${matchReason}, Configured parameter: ${
                existingProduct.parameters
              } (${existingProduct.code}, ID: ${existingProduct.parameter_id})`
            );
          }

          // Use existing ranges if parameter is already configured, otherwise use default ranges
          let ranges;
          if (
            existingProduct &&
            existingProduct.ranges &&
            existingProduct.ranges.length > 0
          ) {
            // Use existing ranges and mark them as selected
            ranges = existingProduct.ranges.map((range: any) => ({
              ...range,
              selected: true, // Mark all existing ranges as selected
            }));
          } else {
            // Use default ranges (all unselected)
            ranges = [
              { score: 0, min: 1, max: 20, selected: false, query: "" },
              { score: 0, min: 21, max: 40, selected: false, query: "" },
              { score: 0, min: 41, max: 60, selected: false, query: "" },
              { score: 0, min: 61, max: 80, selected: false, query: "" },
              { score: 0, min: 81, max: 100, selected: false, query: "" },
            ];
          }

          // Ensure type is always a number
          let paramType = param.parameter_type || 0;
          if (typeof paramType === "string") {
            paramType = parseInt(paramType, 10);
          }
          paramType = Number(paramType);

          // For raw or delta types, formula should be set to code
          let formulaValue = param.parameter_formula || "";
          if (this.isRawOrDelta(paramType)) {
            formulaValue = paramCode;
          }

          // If parameter is already configured, use the configured parameter's details
          // Otherwise use the generic library parameter's details
          if (existingProduct) {
            // Use configured parameter's details (already configured - will be disabled)
            return {
              parameters: existingProduct.parameters,
              unit: existingProduct.unit,
              type: existingProduct.type,
              code: existingProduct.code,
              uploadType: existingProduct.uploadType,
              weight: existingProduct.weight,
              formula: existingProduct.formula,
              calculation: existingProduct.calculation || "",
              criticalParameter: existingProduct.criticalParameter,
              parameter_id: existingProduct.parameter_id,
              ranges: ranges,
              isAlreadyConfigured: true,
            };
          } else {
            // Use generic library parameter's details (new parameter - can be added)
            return {
              parameters: param.parameter_name || param.parameters || "",
              unit: param.parameter_unit || "",
              type: paramType,
              code: paramCode,
              uploadType: this.normalizeUploadType(
                param.uploadType,
                param.upload_source
              ),
              weight: param.parameter_weight || 0,
              formula: formulaValue,
              calculation: param.calculation || param.parameter_calculation || "",
              criticalParameter:
                param.parameter_critical === 1 ||
                param.parameter_critical === true,
              parameter_id: param.parameter_id,
              ranges: ranges,
              isAlreadyConfigured: false,
            };
          }
        })
        .filter((product): product is Product => product !== null); // Filter out nulls (duplicate matches)

      // Store original data for search restoration
      this.originalAddParameterProducts = [...this.addParameterProducts];

      console.log("=== PARAMETER TRANSFORMATION SUMMARY ===");
      console.log(`Total generic library parameters: ${totalGenericParams}`);
      console.log(`Duplicate parameters filtered out: ${duplicateCount}`);
      console.log(
        `Final transformed parameters count: ${this.addParameterProducts.length}`
      );
      console.log("Transformed generic parameters:", this.addParameterProducts);
      const configuredParams = this.addParameterProducts.filter(
        (p) => p.isAlreadyConfigured
      );
      console.log(
        "Parameters already configured count:",
        configuredParams.length
      );
      console.log(
        "Parameters already configured (using configured parameter details, will be disabled):",
        configuredParams.map((p) => ({
          code: p.code,
          parameter_id: p.parameter_id,
          name: p.parameters,
          type: p.type,
          weight: p.weight,
          ranges_count: p.ranges?.length || 0,
          isAlreadyConfigured: p.isAlreadyConfigured,
        }))
      );
    } else {
      console.log("No generic module found or no parameters available");
      // Fallback to empty array if no generic data found
      this.addParameterProducts = [];

      // Show warning message
      this.messageService.add({
        severity: "warn",
        summary: "No Generic Parameters",
        detail:
          "No generic parameters found for this module. Please check the module configuration.",
        life: 5000,
      });
    }

    this.addParameterDialogVisible = true;
    console.log("=== ADD PARAMETER DIALOG OPENED ===");
  }

  closeAddParameterDialog() {
    this.addParameterDialogVisible = false;
    this.addParamGlobalSearchValue = ""; // Clear search value
    this.addParameterProducts = [...this.originalAddParameterProducts]; // Restore original data
  }

  saveAddParameter() {
    console.log("=== SAVING ADD PARAMETER CHANGES ===");
    console.log("Current products before save:", this.products);
    console.log("Add parameter products to save:", this.addParameterProducts);

    // Filter products that have at least one range selected
    const productsWithSelectedRanges = this.addParameterProducts.filter(
      (product) =>
        product.ranges && product.ranges.some((range) => range.selected)
    );

    if (productsWithSelectedRanges.length === 0) {
      this.messageService.add({
        severity: "warn",
        summary: "No Ranges Selected",
        detail:
          "Please select at least one range for each parameter you want to add.",
        life: 5000,
      });
      return;
    }

    // Validate ranges for all products with selected ranges before saving
    let hasValidationError = false;
    for (const product of productsWithSelectedRanges) {
      if (!this.validateRanges(product)) {
        hasValidationError = true;
      }
    }

    if (hasValidationError) {
      // Validation failed, ranges are highlighted, don't save
      this.messageService.add({
        severity: "error",
        summary: "Validation Failed",
        detail:
          "Please fix range validation errors before saving. Invalid ranges are highlighted in red.",
        life: 5000,
      });
      return;
    }

    // Create a new products array by merging existing configured products with new generic parameters
    // Check both by code and parameter_id to avoid duplicates
    const existingProductCodes = new Set(this.products.map((p) => p.code));
    const existingParameterIds = new Set(
      this.products
        .map((p) => p.parameter_id)
        .filter((id) => id !== null && id !== undefined)
    );

    // Filter out products that are already configured (by code or parameter_id)
    const newProducts = productsWithSelectedRanges.filter((p) => {
      const existsByCode = existingProductCodes.has(p.code);
      const existsById =
        p.parameter_id && existingParameterIds.has(p.parameter_id);
      return !existsByCode && !existsById;
    });

    // For each new product, filter out unselected ranges and set formula=code for raw/delta types
    const filteredNewProducts = newProducts.map((product) => {
      // Ensure type is a number
      let productType = product.type;
      if (typeof productType === "string") {
        productType = parseInt(productType, 10);
      }

      const updatedProduct = {
        ...product,
        type: productType, // Ensure type is always a number
        ranges: product.ranges.filter((range) => range.selected),
      };

      console.log("Processing product for add:", {
        code: updatedProduct.code,
        type: updatedProduct.type,
        typeType: typeof updatedProduct.type,
      });

      // Ensure formula is set to code for raw/delta types
      if (this.isRawOrDelta(productType)) {
        updatedProduct.formula = product.code || "";
      }

      return updatedProduct;
    });

    console.log(
      "New products to add (with selected ranges only):",
      filteredNewProducts
    );

    // Check if any products were filtered out because they were already configured
    const alreadyConfiguredCount =
      productsWithSelectedRanges.length - newProducts.length;

    // Add new products to the existing products array
    if (filteredNewProducts.length > 0) {
      console.log(
        "Adding products to main table:",
        filteredNewProducts.map((p) => ({
          code: p.code,
          type: p.type,
          typeType: typeof p.type,
        }))
      );
      this.products.push(...filteredNewProducts);
      console.log(
        "Products after adding:",
        this.products.map((p) => ({
          code: p.code,
          type: p.type,
          typeType: typeof p.type,
        }))
      );

      // Show success message for added parameters
      let message = `${filteredNewProducts.length} new parameter${
        filteredNewProducts.length > 1 ? "s have" : " has"
      } been added successfully with selected ranges.`;
      if (alreadyConfiguredCount > 0) {
        message += ` ${alreadyConfiguredCount} parameter${
          alreadyConfiguredCount > 1 ? "s were" : " was"
        } skipped as ${
          alreadyConfiguredCount > 1 ? "they are" : "it is"
        } already configured.`;
      }

      this.messageService.add({
        severity: "success",
        summary: "Parameters Added",
        detail: message,
        life: 3000,
      });
    } else {
      // Show info message if no new parameters were added
      let message = "No new parameters were added.";
      if (alreadyConfiguredCount > 0) {
        message = `${alreadyConfiguredCount} parameter${
          alreadyConfiguredCount > 1 ? "s were" : " was"
        } skipped as ${
          alreadyConfiguredCount > 1 ? "they are" : "it is"
        } already configured.`;
      }

      this.messageService.add({
        severity: "info",
        summary: "No New Parameters",
        detail: message,
        life: 3000,
      });
    }

    // Update selectedProducts to maintain selection state
    const selectedCodes = new Set(this.selectedProducts?.map((p) => p.code));
    this.selectedProducts = this.products.filter((p) =>
      selectedCodes.has(p.code)
    );

    console.log("Updated products after save:", this.products);
    console.log("Updated selected products:", this.selectedProducts);

    this.closeAddParameterDialog();
  }
  isAddParameterFormValid(): boolean {
    return (
      this.addParameterProducts &&
      this.addParameterProducts.length > 0 &&
      this.addParameterProducts.some(
        (product) => product.parameters && product.parameters.trim() !== ""
      ) &&
      this.addParameterProducts.some(
        (product) =>
          product.ranges && product.ranges.some((range) => range.selected)
      )
    );
  }

  // Method for handling search in add parameter dialog
  onAddParamGlobalSearch(event: any) {
    const value = event.target.value;
    this.addParamGlobalSearchValue = value;

    // Ensure we have original data to work with
    if (
      !this.originalAddParameterProducts ||
      this.originalAddParameterProducts.length === 0
    ) {
      console.warn("No original data available for search");
      return;
    }

    // Filter the addParameterProducts array based on search value
    if (value && value.trim() !== "") {
      const searchTerm = value.toLowerCase().trim();
      this.addParameterProducts = this.originalAddParameterProducts.filter(
        (product) => {
          return (
            (product.parameters &&
              product.parameters.toLowerCase().includes(searchTerm)) ||
            (product.unit && product.unit.toLowerCase().includes(searchTerm)) ||
            (product.code && product.code.toLowerCase().includes(searchTerm)) ||
            (product.type && product.type.toString().includes(searchTerm)) ||
            (product.uploadType &&
              product.uploadType.toLowerCase().includes(searchTerm)) ||
            (product.weight &&
              product.weight.toString().includes(searchTerm)) ||
            (product.formula &&
              product.formula.toLowerCase().includes(searchTerm)) ||
            (product.calculation &&
              product.calculation.toLowerCase().includes(searchTerm))
          );
        }
      );
    } else {
      // If search is empty, restore original data from generic library
      this.restoreOriginalAddParameterData();
    }

    // Force change detection to update the UI
    try {
      this.cdr.detectChanges();
    } catch (error) {
      console.warn("Error in change detection after search:", error);
    }
  }

  // Method to open editor for ranges in add parameter dialog
  openAddParamEditor(range: any) {
    this.editorRange = range;
    this.editorText = range.query || "";
    this.editorVisible = true;
  }

  // Method to handle range selection in add parameter dialog
  onAddParamRangeSelect(product: Product, rangeIndex: number) {
    try {
      const range = product.ranges[rangeIndex];
      // Toggle the selected state
      range.selected = !range.selected;

      if (range.selected) {
        // Set default min/max when checked
        const def = this.getDefaultRangeMinMax(rangeIndex);
        range.min = def.min;
        range.max = def.max;
      }

      // Force change detection to update the UI
      try {
        this.cdr.detectChanges();
      } catch (error) {
        console.warn(
          "Error in change detection after add param range selection:",
          error
        );
      }
    } catch (error) {
      console.error("Error in onAddParamRangeSelect:", error);
    }
  }

  /**
   * Toggle parameter selection in Add Parameter dialog
   * When a parameter is selected (checkbox checked), automatically select the first range
   * @param product The product/parameter to toggle
   */
  onAddParamToggleParameter(product: Product) {
    if (!product || this.isParameterAlreadyConfigured(product)) {
      return; // Don't allow toggling for already configured parameters
    }

    // Check if any range is currently selected
    const hasSelectedRange = product.ranges && product.ranges.some(range => range.selected);

    if (hasSelectedRange) {
      // If ranges are selected, deselect all ranges (unchecking the parameter)
      if (product.ranges) {
        product.ranges.forEach(range => {
          range.selected = false;
        });
      }
    } else {
      // If no ranges are selected, select the first range automatically
      if (product.ranges && product.ranges.length > 0) {
        const firstRange = product.ranges[0];
        firstRange.selected = true;
        
        // Set default min/max values for the first range
        const def = this.getDefaultRangeMinMax(0);
        firstRange.min = def.min;
        firstRange.max = def.max;
        
        console.log(`Auto-selected first range for parameter: ${product.parameters}`);
      }
    }

    // Force change detection to update the UI
    try {
      this.cdr.detectChanges();
    } catch (error) {
      console.warn("Error in change detection after add param toggle:", error);
    }
  }

  /**
   * Check if a parameter has at least one range selected
   * Used in Add Parameter dialog to show checkbox state
   */
  hasAnyRangeSelected(product: Product): boolean {
    return product.ranges && product.ranges.some(range => range.selected);
  }

  // Method to restore original add parameter data
  private restoreOriginalAddParameterData(): void {
    this.addParameterProducts = [...this.originalAddParameterProducts];
    this.cdr.detectChanges();
  }

  // Add Parameter Formula Editor methods
  openAddParamFormulaEditor(product: any) {
    this.addParamFormulaEditorProduct = product;
    this.addParamFormulaEditorText = product.formula || "";
    this.addParamFormulaEditorVisible = true;
  }

  closeAddParamFormulaEditor() {
    this.addParamFormulaEditorVisible = false;
    this.addParamFormulaEditorProduct = null;
    this.addParamFormulaEditorText = "";
  }

  saveAddParamFormulaEditor() {
    if (this.addParamFormulaEditorProduct) {
      // For raw/delta types, formula should always be set to code
      if (this.isRawOrDelta(this.addParamFormulaEditorProduct.type)) {
        this.addParamFormulaEditorProduct.formula =
          this.addParamFormulaEditorProduct.code || "";
      } else {
        this.addParamFormulaEditorProduct.formula =
          this.addParamFormulaEditorText;
      }
    }
    this.closeAddParamFormulaEditor();
  }

  // Add Parameter Query Editor methods
  openAddParamQueryEditor(range: any) {
    this.addParamEditorRange = range;
    this.addParamEditorText = range.query || "";
    this.addParamEditorVisible = true;
  }

  closeAddParamEditor() {
    this.addParamEditorVisible = false;
    this.addParamEditorRange = null;
    this.addParamEditorText = "";
  }

  saveAddParamEditor() {
    if (this.addParamEditorRange) {
      this.addParamEditorRange.query = this.addParamEditorText;
    }
    this.closeAddParamEditor();
  }
}
